/**
 * The workspace label in the header. For everyone it is the same pill as before. Only an owner with more
 * than one workspace gets a switch on that pill. It defaults to Clairvos, and there is no combined view.
 * The browser only chooses which workspace the next request names; the Engine server checks it again.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useAccess } from '@/hooks/useAccess'
import { CURRENT_WORKSPACE } from '@/config/workspace'

const pill = 'inline-flex shrink-0 items-center gap-2 px-2.5 py-0.5 rounded-full border border-(--border) bg-(--card) text-xs font-medium text-(--foreground)'
const dot = <span className="h-2 w-2 rounded-full bg-(--accent)" aria-hidden="true" />

// The header clips and blurs what sits inside it, so the list is drawn on the page body at the pill's position.
function SwitchablePill({ workspaces, value, onChange }) {
  const [open, setOpen] = useState(false)
  const [spot, setSpot] = useState(null)
  const anchor = useRef(null)
  const current = workspaces.find(w => w.id === value) || workspaces[0]

  useEffect(() => {
    if (!open) return undefined
    const close = event => { if (event.type === 'keydown' ? event.key === 'Escape' : !event.target.closest?.('[data-slot="workspace-list"], [data-slot="workspace-switcher"]')) setOpen(false) }
    document.addEventListener('keydown', close)
    document.addEventListener('pointerdown', close)
    return () => { document.removeEventListener('keydown', close); document.removeEventListener('pointerdown', close) }
  }, [open])

  const toggle = () => {
    const box = anchor.current?.getBoundingClientRect?.()
    setSpot({ top: box ? box.bottom + 6 : 0, left: box ? box.left : 0 })
    setOpen(o => !o)
  }
  return (
    <>
      <button ref={anchor} type="button" data-slot="workspace-switcher" aria-haspopup="listbox" aria-expanded={open} aria-label={`Workspace: ${current.label}. Change workspace`}
        onClick={toggle} className={`${pill} cursor-pointer hover:bg-(--muted)`}>
        {dot}
        {current.label}
        <span aria-hidden="true" className="text-(--muted-foreground)">v</span>
      </button>
      {open && spot && createPortal(
        <ul role="listbox" aria-label="Workspace" data-slot="workspace-list" style={{ top: spot.top, left: spot.left }}
          className="fixed z-50 min-w-40 list-none m-0 p-1 rounded-xl border border-(--border) bg-(--card) shadow-lg">
          {workspaces.map(w => (
            <li key={w.id} role="option" aria-selected={w.id === current.id}>
              <button type="button" onClick={() => { setOpen(false); onChange(w.id) }}
                className={`w-full text-left rounded-lg px-3 min-h-[44px] text-sm text-(--foreground) hover:bg-(--muted) ${w.id === current.id ? 'font-medium' : ''}`}>
                {w.label}{w.id === current.id ? ' (current)' : ''}
              </button>
            </li>
          ))}
        </ul>,
        document.body,
      )}
    </>
  )
}

export function WorkspacePill({ label, switchable, workspaces = [], value, onChange }) {
  if (switchable) return <SwitchablePill workspaces={workspaces} value={value} onChange={onChange} />
  return <span className={pill}>{dot}{label}</span>
}

export default function WorkspaceSwitcher() {
  const { access, setWorkspaceId } = useAccess()
  if (!access) return <WorkspacePill label={CURRENT_WORKSPACE.label} />
  return <WorkspacePill label={access.workspaceLabel || CURRENT_WORKSPACE.label} switchable={access.canSwitch} workspaces={access.workspaces} value={access.workspaceId} onChange={setWorkspaceId} />
}
